/**
 * SAMPLE DATA - NOT A REAL LEDGER.
 *
 * Every transaction, vendor, approver and amount below is invented for
 * demonstration. None of it reflects any actual account, contract, payment,
 * or board approval.
 */

import {
  BudgetCategory,
  BudgetReport,
  BudgetTransaction,
  BudgetHistoricalReportMeta,
} from '@/types/entities/community_sub-elects/budgetTransparency';

const createCategory = (
  id: string,
  name: string,
  description: string,
  budgetedAmount: number,
  spentAmount: number,
  color: string,
  status: BudgetCategory['status'],
  lastUpdated: string,
): BudgetCategory => {
  const remainingAmount = Number((budgetedAmount - spentAmount).toFixed(2));
  const percentage =
    budgetedAmount === 0 ? 0 : Number(((spentAmount / budgetedAmount) * 100).toFixed(1));

  return {
    id,
    name,
    description,
    budgetedAmount,
    spentAmount,
    remainingAmount,
    percentage,
    color,
    status,
    lastUpdated,
  };
};

export const glastonburyBudgetReport: BudgetReport = {
  id: '2025-fy',
  title: 'FY 2025 Budget Outlook',
  period: 'FY 2025 (Updated October 2025)',
  type: 'ytd',
  totalBudget: 204000,
  totalSpent: 97350,
  generatedAt: '2025-10-15T12:00:00Z',
  categories: [
    createCategory(
      'roads-infrastructure',
      'Road & Right-of-Way Maintenance',
      'Gravel grading, drainage improvements, culvert repairs, and signage upkeep across 37 lane-miles.',
      58000,
      25400,
      'rgb(37, 99, 235)',
      'on_track',
      '2025-10-12T00:00:00Z',
    ),
    createCategory(
      'snow-seasonal',
      'Snow & Seasonal Response',
      'Pre-season plowing retainers, sand/salt procurement, and equipment standby for Paradise Valley weather.',
      42000,
      11600,
      'rgb(59, 130, 246)',
      'under_budget',
      '2025-10-05T00:00:00Z',
    ),
    createCategory(
      'utilities-shared',
      'Utilities & Shared Services',
      'Rural electric co-op assessments, shared well monitoring, and emergency radio tower power.',
      31500,
      35750,
      'rgb(239, 68, 68)',
      'over_budget',
      '2025-10-08T00:00:00Z',
    ),
    createCategory(
      'governance-admin',
      'Governance & Administrative',
      'Board facilitation, legal counsel on landowner agreements, and insurance premium management.',
      19500,
      8900,
      'rgb(16, 185, 129)',
      'on_track',
      '2025-10-10T00:00:00Z',
    ),
    createCategory(
      'communications-transparency',
      'Communication & Transparency Initiatives',
      'Resident advisory broadcasts, meeting streaming pilots, and compliance documentation digitization.',
      15000,
      6200,
      'rgb(14, 165, 233)',
      'under_budget',
      '2025-10-07T00:00:00Z',
    ),
    createCategory(
      'reserves-strengthening',
      'Reserve Strengthening Contributions',
      'Quarterly transfers into long-term capital and emergency reserves for roadway resilience.',
      38000,
      9500,
      'rgb(107, 114, 128)',
      'on_track',
      '2025-09-30T00:00:00Z',
    ),
  ],
};

export const glastonburyBudgetTransactions: BudgetTransaction[] = [
  {
    id: 'txn-2025-10-05',
    description: 'Gravel road grading contract deposit',
    amount: 18750,
    category: 'Road & Right-of-Way Maintenance',
    type: 'expense',
    date: '2025-10-05T00:00:00Z',
    vendor: 'Mountain West Civil Services',
    approvedBy: 'Sample Treasurer',
  },
  {
    id: 'txn-2025-09-30',
    description: 'Snow removal retainer - Winter 2025/26',
    amount: 9800,
    category: 'Snow & Seasonal Response',
    type: 'expense',
    date: '2025-09-30T00:00:00Z',
    vendor: 'Yellowstone Snow & Ice Cooperative',
    approvedBy: 'Operations Committee',
  },
  {
    id: 'txn-2025-09-25',
    description: 'Rural electric co-op assessment (Q3)',
    amount: 12450,
    category: 'Utilities & Shared Services',
    type: 'expense',
    date: '2025-09-25T00:00:00Z',
    vendor: 'Paradise Valley Co-op',
    approvedBy: 'Finance Subpanel',
  },
  {
    id: 'txn-2025-09-18',
    description: 'Community transparency portal pilot',
    amount: 4250,
    category: 'Communication & Transparency Initiatives',
    type: 'expense',
    date: '2025-09-18T00:00:00Z',
    vendor: 'CivicSignal Labs',
    approvedBy: 'Board Secretary',
  },
  {
    id: 'txn-2025-09-15',
    description: 'Quarterly landowner assessments collected',
    amount: 52350,
    category: 'General Assessments',
    type: 'income',
    date: '2025-09-15T00:00:00Z',
    approvedBy: 'Automated Collections',
  },
  {
    id: 'txn-2025-09-09',
    description: 'Reserve fund transfer - Q3 top-up',
    amount: 9500,
    category: 'Reserve Strengthening Contributions',
    type: 'expense',
    date: '2025-09-09T00:00:00Z',
    approvedBy: 'Sample Treasurer',
  },
];

export const glastonburyHistoricalReports: BudgetHistoricalReportMeta[] = [
  {
    id: 'report-2025-q3-prep',
    title: '2025 Q3 Budget Positioning Summary',
    period: 'Q3 2025',
    type: 'quarterly',
    generatedAt: '2025-10-01T15:30:00Z',
  },
  {
    id: 'report-2025-midyear',
    title: '2025 Mid-Year Financial Health Update',
    period: 'Mid-Year 2025',
    type: 'ytd',
    generatedAt: '2025-07-10T09:15:00Z',
  },
  {
    id: 'report-2024-annual',
    title: '2024 Annual Financial Statement',
    period: 'FY 2024',
    type: 'annual',
    generatedAt: '2025-01-12T11:00:00Z',
  },
  {
    id: 'report-2024-q3',
    title: '2024 Q3 Budget Performance Review',
    period: 'Q3 2024',
    type: 'quarterly',
    generatedAt: '2024-10-05T13:45:00Z',
  },
];
